// The sky message (skywriter.go server-side): the ad circus's headline,
// smoke-typed high above both cloud decks and laid FLAT across the sky, the
// way the barnstormers wrote over county fairs — you read it from the deck,
// or from a cockpit with a glance straight up. The server owns the flying
// and the font; this class just hangs smoke wherever the server says a dot
// happened, so the client never needs the text, the letter grid, or the
// wind debate.
//
// The smoke itself is kin to the contrails (effects.js PlaneContrails):
// soft translucent white, no hard edges — but where a contrail is a rope,
// a skytyped dot is a BILLOW: each dot is a little cluster of overlapping
// translucent blocks at seeded offsets that bloom outward over the first
// couple of seconds, then hang. depthWrite stays off so neighbouring
// billows melt together into strokes instead of cutting seams.
//
// Round-lived like the battlefield scars: fed by the welcome handshake
// (late joiners see every letter already hanging), grown live by one-shot
// "swp" events, wiped on roundStart. One InstancedMesh draws the lot.

import * as THREE from "three";
import { touchInstances } from "./instance_upload.js";

const MAX_DOTS = 640;      // 20 chars × 5×5 font, with headroom
const BLOBS_PER_DOT = 7;   // one core + a tumble of satellites per dot
const BLOOM_SEC = 3.2;     // how long a fresh billow takes to fatten up

// Cheap deterministic hash off the dot's position, so every client grows
// identical smoke — no per-client RNG, no netcode.
function h(x, y, z, salt) {
  const v = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719 + salt * 53.13) * 43758.5453;
  return v - Math.floor(v);
}

export class Skywriting {
  constructor(scene, dotM = 20) {
    this.dotM = dotM;
    const geo = new THREE.BoxGeometry(1, 1, 1);
    // The contrail recipe — translucent unlit white, depthWrite off — with
    // fog switched off: the message lives 3 km up and several km from most
    // eyes, and an ad the haze can eat is an ad nobody paid for.
    const mat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.62, // a shade denser than a contrail: ad smoke is bought by the pound
      depthWrite: false,
      fog: false,
    });
    this.mesh = new THREE.InstancedMesh(geo, mat, MAX_DOTS * BLOBS_PER_DOT);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(MAX_DOTS * BLOBS_PER_DOT * 3), 3);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.ids = new Set(); // dedupe: welcome replay + live event can overlap
    this.dots = [];       // { x, y, z, age }
    this._m = new THREE.Matrix4();
    this._q = new THREE.Quaternion();
    this._e = new THREE.Euler();
    this._p = new THREE.Vector3();
    this._s = new THREE.Vector3();
    this._cA = new THREE.Color(0xffffff);
    this._cB = new THREE.Color(0xcfdff0); // paler than the contrails' fade: edges melt into sky
    this._cT = new THREE.Color();
  }

  clear() {
    this.ids.clear();
    this.dots.length = 0;
    this.mesh.count = 0;
  }

  setFromWelcome(list) {
    this.clear();
    for (const p of list ?? []) this.add(p.id, p.x, p.y, p.z, true);
  }

  /** One more dot of the message. mature=true (welcome replay) skips the bloom. */
  add(id, x, y, z, mature = false) {
    if (this.ids.has(id) || this.dots.length >= MAX_DOTS) return;
    this.ids.add(id);
    const i = this.dots.length;
    this.dots.push({ x, y, z, age: mature ? BLOOM_SEC + 1 : 0 });
    this._placeDot(i);
    this.mesh.count = this.dots.length * BLOBS_PER_DOT;
  }

  // Lay a dot's blob tumble for its current age: a fat core that swells to
  // the full grid pitch and a ring of satellites that drift outward as they
  // grow, far enough that neighbouring dots' wisps overlap and a row of
  // dots reads as one continuous rolling cloud-stroke — smoke billowing,
  // not beads on a string.
  _placeDot(i) {
    const d = this.dots[i];
    const t = Math.min(d.age / BLOOM_SEC, 1);
    const grow = t * t * (3 - 2 * t); // smoothstep — fast billow, gentle settle
    for (let b = 0; b < BLOBS_PER_DOT; b++) {
      const idx = i * BLOBS_PER_DOT + b;
      // Satellite b of this dot: seeded direction, a touch of drift with age.
      const a1 = h(d.x, d.y, d.z, b) * Math.PI * 2;
      const a2 = (h(d.x, d.y, d.z, b + 9) - 0.5) * 1.6;
      const reach = b === 0 ? 0 : this.dotM * (0.24 + 0.4 * h(d.x, d.y, d.z, b + 17)) * (0.4 + 0.6 * grow);
      this._p.set(
        d.x + Math.cos(a1) * Math.cos(a2) * reach,
        d.y + Math.sin(a2) * reach * 0.5, // billows squash a little, like clouds do
        d.z + Math.sin(a1) * Math.cos(a2) * reach,
      );
      const base = b === 0 ? 0.85 : 0.4 + 0.3 * h(d.x, d.y, d.z, b + 31);
      const s = this.dotM * base * (0.12 + 0.88 * grow);
      this._e.set(h(d.x, d.y, d.z, b + 43) * 0.9, h(d.x, d.y, d.z, b + 57) * 0.9, 0);
      this._q.setFromEuler(this._e);
      this._s.set(s * 1.3, s * 0.75, s); // wider than tall: a puff, not a die
      this._m.compose(this._p, this._q, this._s);
      this.mesh.setMatrixAt(idx, this._m);
      // Core bright; outer wisps fade toward a pale sky tone so the stroke's
      // edge melts into the blue instead of ending in a facet.
      this._cT.copy(this._cA).lerp(this._cB, b === 0 ? 0.08 : 0.35 + 0.45 * h(d.x, d.y, d.z, b + 71));
      this.mesh.setColorAt(idx, this._cT);
    }
    // Send just this dot's blobs: a message is up to 4480 of them, and the
    // whole sky re-sent for every dot still blooming was the bill.
    touchInstances(this.mesh.instanceMatrix, i * BLOBS_PER_DOT, BLOBS_PER_DOT);
    touchInstances(this.mesh.instanceColor, i * BLOBS_PER_DOT, BLOBS_PER_DOT);
  }

  update(dt) {
    // Only blooming billows need per-frame matrices; the settled sky is free.
    for (let i = 0; i < this.dots.length; i++) {
      const d = this.dots[i];
      if (d.age > BLOOM_SEC) continue;
      d.age += dt;
      this._placeDot(i);
    }
  }
}
